/* eslint-disable camelcase */
import { Injectable } from '@angular/core';
import { ValidationsExamGeneral } from '@generated/base-types';
import { takeGraphQLResult } from '@new/common/operators/take-graphql-response';
import { ManagerErrorCode, parseError } from '@new/common/utils/error-parser';
import { isDefined } from '@new/common/utils/type-guards/is-defined';
import { TranslateService } from '@ngx-translate/core';
import type { StateContext } from '@ngxs/store';
import { Action, State, Store } from '@ngxs/store';
import { Observable } from 'rxjs';
import { catchError, filter, map, switchMap } from 'rxjs/operators';
import { ToastsService } from '../../../../common/services/toasts.service';
import { LockState } from '../../../lib/types';
import { CreateExamGQL } from '../../lib/services/exam/create-exam.generated';
import { DeleteExamGQL } from '../../lib/services/exam/delete-exam.generated';
import { LoadExamListGQL } from '../../lib/services/exam/load-exams.generated';
import { LockExamGQL } from '../../lib/services/exam/lock-exam.generated';
import { PublishExamGQL } from '../../lib/services/exam/publish-exam.generated';
import { SendToMeasuredGQL } from '../../lib/services/exam/send-to-measured.generated';
import { UnlockExamGQL } from '../../lib/services/exam/unlock-exam.generated';
import { UpdateExamGQL } from '../../lib/services/exam/update-exam.generated';
import { ExamState } from '../exam/exam.state';
import { ResetSelectedCandidateIds } from '../exam-candidates/exam-candidates.actions';
import {
  ResetTableState,
  UpdateExamQuestionGroupLockedState
} from '../exam-question-groups/exam-question-groups.actions';
import { PoolExamAdded, PoolExamDeleted } from '../pool/pool.actions';
import { selectPoolId } from '../pool/pool.state.model';
import * as Act from './exams.actions';
import { examsStateDefaults, ExamsStateModel } from './exams.state.model';

@State<ExamsStateModel>({
  name: 'exams',
  defaults: examsStateDefaults,
  children: [ExamState]
})
@Injectable()
export class ExamsState {
  constructor(
    private readonly loadExamsService: LoadExamListGQL,
    private readonly createExamService: CreateExamGQL,
    private readonly updateExamService: UpdateExamGQL,
    private readonly deleteExamService: DeleteExamGQL,
    private readonly unlockExamService: UnlockExamGQL,
    private readonly lockExamService: LockExamGQL,
    private readonly publishExamAsSelfAssessmentService: PublishExamGQL,
    private readonly sendToMeasuredService: SendToMeasuredGQL,
    private readonly toasts: ToastsService,
    private readonly translate: TranslateService,
    private readonly store: Store
  ) {}

  /* Query actions */

  @Action(Act.LoadExams)
  public loadExams(ctx: StateContext<ExamsStateModel>): Observable<void> {
    ctx.patchState({
      items: { ...ctx.getState().items, query: { state: 'loading' } }
    });

    return this.store.select(selectPoolId).pipe(
      filter(isDefined),
      switchMap(poolId => this.loadExamsService.fetch({ poolId })),
      takeGraphQLResult(),
      switchMap(res => ctx.dispatch(new Act.LoadExamsSuccess(res.pool.exams))),
      catchError((err: unknown) =>
        ctx.dispatch(new Act.LoadExamsFailure(parseError(err)))
      )
    );
  }

  @Action(Act.LoadExamsSuccess)
  public loadExamsSuccess(
    ctx: StateContext<ExamsStateModel>,
    { exams }: Act.LoadExamsSuccess
  ): void {
    ctx.patchState({
      items: {
        ...ctx.getState().items,
        data: exams,
        query: { state: 'success' }
      }
    });
  }

  @Action(Act.LoadExamsFailure)
  public loadExamsFailure(
    ctx: StateContext<ExamsStateModel>,
    { error }: Act.LoadExamsFailure
  ): void {
    ctx.patchState({
      items: { ...ctx.getState().items, query: { state: 'failure', error } }
    });
  }

  @Action(Act.SetSelectedExamIds)
  public setSelectedExamIds(
    ctx: StateContext<ExamsStateModel>,
    { ids }: Act.SetSelectedExamIds
  ): void {
    ctx.patchState({ selectedIds: ids });
  }

  /* Mutation actions */

  @Action(Act.Failure)
  public failure(
    ctx: StateContext<ExamsStateModel>,
    { error, validationMessage }: Act.Failure
  ): void {
    ctx.patchState({
      items: { ...ctx.getState().items, mutation: { state: 'failure', error } }
    });

    this.toasts.addWarning(
      validationMessage ?? this.translate.instant('error_modal.title')
    );
  }

  @Action(Act.CreateExam)
  public createExam(
    ctx: StateContext<ExamsStateModel>,
    { examInput }: Act.CreateExam
  ): Observable<void> {
    ctx.patchState({
      items: { ...ctx.getState().items, mutation: { state: 'loading' } }
    });

    return this.store.select(selectPoolId).pipe(
      filter(isDefined),
      switchMap(poolId =>
        this.createExamService.mutate({
          attributes: {
            ...examInput,
            poolId: Number.parseInt(poolId)
          }
        })
      ),
      takeGraphQLResult(),
      map(res => res?.createExam?.exam),
      filter(isDefined),
      switchMap(exam => ctx.dispatch(new Act.CreateExamSuccess(exam))),
      catchError((err: unknown) =>
        ctx.dispatch(new Act.Failure(parseError(err)))
      )
    );
  }

  @Action(Act.CreateExamSuccess)
  public createExamSuccess(
    ctx: StateContext<ExamsStateModel>,
    { exam }: Act.CreateExamSuccess
  ): void {
    const existing = ctx.getState().items.data ?? [];
    const merged = [...existing, exam];
    merged.sort((a, b) => {
      const at = a.date ? Date.parse(a.date) : 0;
      const bt = b.date ? Date.parse(b.date) : 0;

      return bt - at;
    });

    ctx.patchState({
      items: {
        ...ctx.getState().items,
        data: merged,
        mutation: { state: 'success' }
      }
    });

    ctx.dispatch(new PoolExamAdded(exam));

    this.toasts.addSuccess(
      this.translate.instant('toast_success_messages.create', {
        resource: this.translate.instant(
          'exam_management.exam_list.messages.exam'
        )
      })
    );
  }

  @Action(Act.UpdateExam)
  public updateExam(
    ctx: StateContext<ExamsStateModel>,
    { examId, examInput }: Act.UpdateExam
  ): Observable<void> {
    ctx.patchState({
      items: { ...ctx.getState().items, mutation: { state: 'loading' } }
    });

    return this.store.select(selectPoolId).pipe(
      filter(isDefined),
      switchMap(poolId =>
        this.updateExamService.mutate({
          id: examId,
          attributes: {
            ...examInput,
            poolId: Number.parseInt(poolId)
          }
        })
      ),
      takeGraphQLResult(),
      map(res => res?.updateExam?.exam),
      filter(isDefined),
      switchMap(exam => ctx.dispatch(new Act.UpdateExamSuccess(exam))),
      catchError((err: unknown) =>
        ctx.dispatch(new Act.Failure(parseError(err)))
      )
    );
  }

  @Action(Act.UpdateExamSuccess)
  public updateExamSuccess(
    ctx: StateContext<ExamsStateModel>,
    { exam, lockState }: Act.UpdateExamSuccess
  ): void {
    ctx.patchState({
      items: {
        ...ctx.getState().items,
        data: ctx
          .getState()
          .items.data?.map(e => (e.id === exam.id ? exam : e)),
        mutation: { state: 'success' }
      }
    });

    if (lockState) {
      this.store.dispatch(
        new UpdateExamQuestionGroupLockedState(exam.id, lockState)
      );

      const messageKey =
        lockState === LockState.Locked
          ? 'exam_management.exam_list.messages.exam_locked'
          : 'exam_management.exam_list.messages.exam_unlocked';

      this.toasts.addSuccess(this.translate.instant(messageKey));
    } else {
      this.toasts.addSuccess(
        this.translate.instant('toast_success_messages.update', {
          resource: this.translate.instant(
            'exam_management.exam_list.messages.exam'
          )
        })
      );
    }
  }

  @Action(Act.DeleteExam)
  public deleteExam(
    ctx: StateContext<ExamsStateModel>,
    { examId }: Act.DeleteExam
  ): Observable<void> {
    ctx.patchState({
      items: { ...ctx.getState().items, mutation: { state: 'loading' } }
    });

    return this.store.select(selectPoolId).pipe(
      filter(isDefined),
      switchMap(poolId =>
        this.deleteExamService.mutate({ id: examId, poolId })
      ),
      takeGraphQLResult(),
      switchMap(() => ctx.dispatch(new Act.DeleteExamSuccess(examId))),
      catchError((err: unknown) =>
        ctx.dispatch(new Act.Failure(parseError(err)))
      )
    );
  }

  @Action(Act.DeleteExamSuccess)
  public deleteExamSuccess(
    ctx: StateContext<ExamsStateModel>,
    { examId }: Act.DeleteExamSuccess
  ): void {
    ctx.patchState({
      items: {
        ...ctx.getState().items,
        data: ctx.getState().items.data?.filter(e => e.id !== examId),
        mutation: { state: 'success' }
      },
      selectedIds: []
    });

    ctx.dispatch(new PoolExamDeleted(examId));
    this.store.dispatch(new ResetTableState());
    this.store.dispatch(new ResetSelectedCandidateIds());

    this.toasts.addSuccess(
      this.translate.instant('toast_success_messages.destroy', {
        resource: this.translate.instant(
          'exam_management.exam_list.messages.exam'
        )
      })
    );
  }

  @Action(Act.LockExam)
  public lockExam(
    ctx: StateContext<ExamsStateModel>,
    { examId }: Act.LockExam
  ): Observable<void> {
    ctx.patchState({
      items: { ...ctx.getState().items, mutation: { state: 'loading' } }
    });

    return this.lockExamService.mutate({ id: examId }).pipe(
      takeGraphQLResult(),
      map(res =>
        res?.lockExam?.successful === true && isDefined(res.lockExam.exam)
          ? res.lockExam.exam
          : undefined
      ),
      filter(isDefined),
      switchMap(exam =>
        ctx.dispatch(new Act.UpdateExamSuccess(exam, LockState.Locked))
      ),
      catchError((err: unknown) => {
        const error = parseError(err);

        if (error.code === ManagerErrorCode.ValidationError) {
          const examValidationError =
            error.validations as unknown as ValidationsExamGeneral;
          const validationMessage = examValidationError.general?.[0]?.message;

          return ctx.dispatch(
            new Act.Failure(error, validationMessage ?? undefined)
          );
        } else {
          return ctx.dispatch(new Act.Failure(error));
        }
      })
    );
  }

  @Action(Act.UnlockExam)
  public unlockExam(
    ctx: StateContext<ExamsStateModel>,
    { examId, unlockCode }: Act.UnlockExam
  ): Observable<void> {
    ctx.patchState({
      items: { ...ctx.getState().items, mutation: { state: 'loading' } }
    });

    return this.unlockExamService.mutate({ id: examId, unlockCode }).pipe(
      takeGraphQLResult(),
      map(res =>
        res?.unlockExam?.successful === true && isDefined(res.unlockExam.exam)
          ? res.unlockExam.exam
          : undefined
      ),
      filter(isDefined),
      switchMap(exam =>
        ctx.dispatch(new Act.UpdateExamSuccess(exam, LockState.Unlocked))
      ),
      catchError((err: unknown) => {
        const error = parseError(err);

        if (error.code === ManagerErrorCode.ValidationError) {
          const examValidationError =
            error.validations as unknown as ValidationsExamGeneral;
          const validationMessage = examValidationError.general?.[0]?.message;

          return ctx.dispatch(
            new Act.Failure(error, validationMessage ?? undefined)
          );
        } else {
          return ctx.dispatch(new Act.Failure(error));
        }
      })
    );
  }

  @Action(Act.PublishExamAsSelfAssessment)
  public publishExamAsSelfAssessment(
    ctx: StateContext<ExamsStateModel>,
    { examId, password }: Act.PublishExamAsSelfAssessment
  ): Observable<void> {
    ctx.patchState({
      items: { ...ctx.getState().items, mutation: { state: 'loading' } }
    });

    const id = Number.parseInt(examId, 10);

    return this.publishExamAsSelfAssessmentService
      .mutate({ id, password })
      .pipe(
        takeGraphQLResult(),
        switchMap(() =>
          ctx.dispatch(new Act.PublishExamAsSelfAssessmentSuccess(examId))
        ),
        catchError((err: unknown) => {
          const error = parseError(err);

          if (error.code === ManagerErrorCode.ValidationError) {
            const examValidationError =
              error.validations as unknown as ValidationsExamGeneral;
            const validationMessage = examValidationError.general?.[0]?.message;

            return ctx.dispatch(
              new Act.Failure(error, validationMessage ?? undefined)
            );
          } else {
            return ctx.dispatch(new Act.Failure(error));
          }
        })
      );
  }

  @Action(Act.PublishExamAsSelfAssessmentSuccess)
  public publishExamAsSelfAssessmentSuccess(
    ctx: StateContext<ExamsStateModel>,
    { examId }: Act.PublishExamAsSelfAssessmentSuccess
  ): void {
    ctx.patchState({
      items: {
        ...ctx.getState().items,
        data: ctx
          .getState()
          .items.data?.map(e => (e.id === examId ? { ...e, locked: true } : e)),
        mutation: { state: 'success' }
      }
    });

    this.toasts.addSuccess(
      this.translate.instant('toast_success_messages.publish', {
        resource: this.translate.instant(
          'exam_management.exam_list.messages.exam'
        )
      })
    );
  }

  @Action(Act.SendExamToMeasured)
  public sendExamToMeasured(
    ctx: StateContext<ExamsStateModel>,
    { examId, provisionSettings }: Act.SendExamToMeasured
  ): Observable<void> {
    ctx.patchState({
      items: { ...ctx.getState().items, mutation: { state: 'loading' } }
    });

    return this.sendToMeasuredService.mutate({ ...provisionSettings }).pipe(
      takeGraphQLResult(),
      switchMap(() => ctx.dispatch(new Act.SendExamToMeasuredSuccess(examId))),
      catchError((err: unknown) =>
        ctx.dispatch(new Act.Failure(parseError(err)))
      )
    );
  }

  @Action(Act.SendExamToMeasuredSuccess)
  public sendExamToMeasuredSuccess(
    ctx: StateContext<ExamsStateModel>,
    { examId }: Act.SendExamToMeasuredSuccess
  ): void {
    ctx.patchState({
      items: {
        ...ctx.getState().items,
        data: ctx
          .getState()
          .items.data?.map(exam =>
            exam.id === examId
              ? { ...exam, provisioned: true, locked: true }
              : exam
          )
      }
    });

    ctx.dispatch(
      new UpdateExamQuestionGroupLockedState(examId, LockState.Locked)
    );

    this.toasts.addSuccess(
      this.translate.instant(
        'exam_management.exam_list.exam_provision_modal.success'
      )
    );
  }
}
